import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PaymentAuditRecord {
  id: string;
  payment_number: string | null;
  amount: number;
  paid_amount: number;
  paid_at: string;
  payment_type: string | null;
  deal_name: string;
  contact_name: string;
  paid_by_user: string | null;
  paid_by_email: string | null;
  business_unit: string | null;
  currency: string;
}

interface UsePaymentAuditReportOptions {
  startDate?: string;
  endDate?: string;
  paidByUserId?: string;
  paymentType?: string;
  businessUnit?: string;
}

export function usePaymentAuditReport({
  startDate,
  endDate,
  paidByUserId,
  paymentType,
  businessUnit,
}: UsePaymentAuditReportOptions = {}) {
  return useQuery({
    queryKey: ['payment-audit-report', startDate, endDate, paidByUserId, paymentType, businessUnit],
    queryFn: async () => {
      // Build the query string
      const selectQuery = `
        id,
        payment_number,
        amount,
        paid_amount,
        paid_at,
        payment_type,
        deals!inner(
          name,
          business_unit,
          currency,
          contacts(first_name, last_name)
        ),
        paid_by_user:app_users!paid_by(
          display_name,
          email
        )
      `;

      let query = supabase
        .from('payments')
        .select(selectQuery)
        .eq('status', 'paid')
        .not('paid_at', 'is', null)
        .order('paid_at', { ascending: false });

      if (startDate) {
        query = query.gte('paid_at', startDate);
      }
      if (endDate) {
        query = query.lte('paid_at', endDate + 'T23:59:59');
      }
      if (paidByUserId && paidByUserId !== 'all') {
        query = query.eq('paid_by', paidByUserId);
      }
      if (paymentType && paymentType !== 'all') {
        query = query.eq('payment_type', paymentType);
      }

      const { data, error } = await query.limit(500);

      if (error) throw error;

      // Transform the data - use any to avoid TypeScript deep instantiation issue
      const result: PaymentAuditRecord[] = ((data as any[]) || []).map((payment) => {
        const deal = payment.deals;
        const paidByUser = payment.paid_by_user;
        
        return {
          id: payment.id,
          payment_number: payment.payment_number,
          amount: payment.amount,
          paid_amount: payment.paid_amount || 0,
          paid_at: payment.paid_at || '',
          payment_type: payment.payment_type,
          currency: deal?.currency || 'USD',
          deal_name: deal?.name || '-',
          contact_name: deal?.contacts 
            ? `${deal.contacts.first_name || ''} ${deal.contacts.last_name || ''}`.trim() 
            : '-',
          business_unit: deal?.business_unit || null,
          paid_by_user: paidByUser?.display_name || null,
          paid_by_email: paidByUser?.email || null,
        };
      });
      
      return result;
    },
  });
}

export function useAppUsers() {
  return useQuery({
    queryKey: ['app-users-for-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_users')
        .select('id, display_name, email')
        .eq('active', true)
        .order('display_name');

      if (error) throw error;
      return data || [];
    },
  });
}
